import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { personalInfo } from "../data/mock";
import { Mail, Linkedin, ArrowUp, Users, Flame } from "lucide-react";
import { API_BASE_URL } from "../config/api";

const Footer = () => {
  const [visitorCount, setVisitorCount] = useState(null);

  useEffect(() => {
    const fetchCount = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/analytics/visitor-count`);
        const data = await res.json();
        if (data.success) setVisitorCount(data.count);
      } catch {
        // Silent fail
      }
    };
    fetchCount();
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-black text-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-16">
        <div className="grid md:grid-cols-3 gap-12">
          {/* Brand */}
          <div className="space-y-6">
            <div>
              <h3 className="text-3xl font-light tracking-tight mb-2">
                {personalInfo.name}
              </h3>
              <p className="text-gray-400 font-light">
                {personalInfo.title}
              </p>
            </div>
            <p className="text-gray-300 font-light leading-relaxed">
              {personalInfo.tagline}
            </p>
            <div className="text-sm text-gray-400 font-light">
              {personalInfo.motto}
            </div>
          </div>

          {/* Quick Links */}
          <div className="space-y-6">
            <h4 className="text-lg font-light tracking-wide">Quick Links</h4>
            <nav className="space-y-3">
              {[
                { label: "About", id: "about" },
                { label: "Storyteller", id: "storyteller" },
                { label: "Skills", id: "skills" },
                { label: "Experience", id: "experience" },
                { label: "Projects", id: "projects" },
                { label: "YAHFFIRMATIONS", id: "yahffirmations" },
                { label: "INSPIYAH MAIL", id: "blog" },
                { label: "FAQ", id: "faq", isRoute: true },
                { label: "Contact", id: "contact" }
              ].map((link) => (
                link.isRoute ? (
                  <Link
                    key={link.id}
                    to={`/${link.id}`}
                    className="block text-gray-400 font-light hover:text-white transition-colors duration-300 text-left"
                    data-testid={`footer-link-${link.id}`}
                  >
                    {link.label}
                  </Link>
                ) : (
                  <button
                    key={link.id}
                    onClick={() => {
                      const element = document.getElementById(link.id);
                      if (element) {
                        element.scrollIntoView({ behavior: "smooth" });
                      }
                    }}
                    className="block text-gray-400 font-light hover:text-white transition-colors duration-300 text-left"
                    data-testid={`footer-link-${link.id}`}
                  >
                    {link.label}
                  </button>
                )
              ))}
            </nav>
          </div>

          {/* Contact Info */}
          <div className="space-y-6">
            <h4 className="text-lg font-light tracking-wide">Connect</h4>
            <div className="space-y-4">
              <a
                href={`mailto:${personalInfo.email}`}
                className="flex items-center space-x-3 text-gray-400 hover:text-white transition-colors duration-300 group"
              >
                <Mail size={18} className="group-hover:scale-110 transition-transform duration-300" />
                <span className="font-light">{personalInfo.email}</span>
              </a>
              <a
                href={personalInfo.linkedin}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center space-x-3 text-gray-400 hover:text-white transition-colors duration-300 group"
              >
                <Linkedin size={18} className="group-hover:scale-110 transition-transform duration-300" />
                <span className="font-light">LinkedIn Profile</span>
              </a>
              <a
                href="https://www.inspiyah.com"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center space-x-3 text-gray-400 hover:text-white transition-colors duration-300 group"
              >
                <Flame size={18} className="group-hover:scale-110 transition-transform duration-300" />
                <span className="font-light">INSPIYAH.com</span>
              </a>
            </div>
            
            <div className="pt-4">
              <p className="text-gray-400 font-light text-sm">
                {personalInfo.location}
              </p>
            </div>
          </div>
        </div>

        {/* Copyright Notice */}
        <div className="border-t border-gray-800 mt-12 pt-8">
          {/* Visitor Counter */}
          {visitorCount !== null && visitorCount > 0 && (
            <div className="flex items-center justify-center gap-2 mb-6">
              <Users size={14} className="text-gray-500" />
              <p data-testid="visitor-counter" className="text-sm text-gray-500 font-light tracking-wide">
                <span className="text-yellow-400 font-medium">{visitorCount.toLocaleString()}</span> soul{visitorCount !== 1 ? 's' : ''} have entered this space
              </p>
            </div>
          )}

          <div className="bg-gray-900 rounded-lg p-6 mb-6">
            <p className="text-gray-300 text-sm leading-relaxed mb-4">
              <strong className="text-white">Copyright © 1988 CT / KEEPER OF THE FIYAH. All Rights Reserved.</strong>
            </p>
            <p className="text-gray-300 text-sm leading-relaxed mb-4">
              <strong className="text-white">Effective Date:</strong> January 1, 1988
            </p>
            <p className="text-gray-300 text-sm leading-relaxed mb-4">
              Unauthorized reproduction, distribution, or adaptation of any material is strictly prohibited. 
              This includes, but is not limited to, all text, images, graphics, logos, digital assets, research materials, and any other content 
              on this website or associated platforms. No content may be copied, scraped, indexed, downloaded, ingested, or used for the training, 
              development, or refinement of artificial intelligence systems, machine-learning models, or automated datasets without explicit prior 
              written permission.
            </p>
            <p className="text-gray-300 text-sm leading-relaxed mb-4">
              <strong className="text-white">AI/ML Prohibition:</strong> This prohibition extends to all forms of AI including but not limited to: 
              Large Language Models (LLMs), Generative AI, Neural Networks, Web Scrapers, Crawlers, and any automated data collection systems. 
              Use of this content to generate derivative works—whether through AI prompts, algorithms, or automated tools—is strictly prohibited.
            </p>
            <p className="text-gray-300 text-sm leading-relaxed mb-4">
              <strong className="text-white">Enforcement:</strong> Violations will be prosecuted to the fullest extent of the law, including pursuit 
              of injunctive relief and monetary damages under the Digital Millennium Copyright Act (DMCA) and applicable international treaties.
            </p>
            <p className="text-gray-300 text-sm leading-relaxed mb-4">
              <strong className="text-white">Jurisdiction:</strong> This policy is governed by the laws of the Universe.
            </p>
            <p className="text-gray-300 text-sm leading-relaxed">
              <strong className="text-white">For Licensing Inquiries or Permission Requests:</strong>{' '}
              <a href="mailto:support@inspiyah.com" className="text-purple-400 hover:text-purple-300 transition-colors">
                support@inspiyah.com
              </a>
            </p>
          </div>
          
          {/* Bottom Bar */}
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            {/* Copyright and Legal Links */}
            <div className="flex flex-col md:flex-row items-center gap-4 text-sm">
              <div className="text-gray-400 font-light">
                © {currentYear} {personalInfo.name}. All rights reserved.
              </div>
              <div className="flex flex-wrap items-center gap-3 text-sm">
                <span className="hidden md:inline text-gray-600">|</span>
                <Link 
                  to="/privacy-policy" 
                  className="text-gray-400 hover:text-white transition-colors duration-300 font-light"
                >
                  Privacy Policy
                </Link>
                <span className="text-gray-600">•</span>
                <Link 
                  to="/data-processing-addendum" 
                  className="text-gray-400 hover:text-white transition-colors duration-300 font-light"
                >
                  DPA
                </Link>
                <span className="text-gray-600">•</span>
                <Link 
                  to="/terms-of-service" 
                  className="text-gray-400 hover:text-white transition-colors duration-300 font-light"
                >
                  Terms of Service
                </Link>
                <span className="text-gray-600">•</span>
                <Link 
                  to="/ai-usage-policy" 
                  className="text-gray-400 hover:text-white transition-colors duration-300 font-light"
                >
                  AI Usage Policy
                </Link>
                <span className="text-gray-600">•</span>
                <Link 
                  to="/disclaimer" 
                  className="text-gray-400 hover:text-white transition-colors duration-300 font-light"
                >
                  Disclaimer
                </Link>
                <span className="text-gray-600">•</span>
                <Link 
                  to="/faq" 
                  data-testid="footer-legal-faq"
                  className="text-gray-400 hover:text-white transition-colors duration-300 font-light"
                >
                  FAQ
                </Link>
              </div>
            </div>
            
            {/* Scroll to Top */}
            <div className="flex items-center gap-6">
              <div className="text-gray-400 font-light text-sm hidden md:block">
                {personalInfo.philosophy}
              </div>
              <button
                onClick={scrollToTop}
                className="p-2 bg-gray-800 hover:bg-gray-700 rounded-lg transition-colors duration-300 group"
                aria-label="Scroll to top"
              >
                <ArrowUp 
                  size={16} 
                  className="text-gray-400 group-hover:text-white group-hover:-translate-y-1 transition-all duration-300" 
                />
              </button>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;